import React, { useEffect, useRef, useState } from "react";
import { Button } from "../../../shadcn/components/ui/button";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Input } from "../../../shadcn/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../shadcn/components/ui/table";

type DataTableProps<T> = {
  data: T[];
  columns: ColumnDef<T>[];
  actions?: (row: T) => React.ReactNode;
  searchComponent?: React.ReactNode;
  filterComponent?: React.ReactNode;
  loading?: boolean;
};

export default function BiddersDataTable<T>({
  data,
  columns,
  actions,
  searchComponent,
  filterComponent,
  loading,
}: DataTableProps<T>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [searchValue, setSearchValue] = useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter: searchValue,
    },
  });

  const divRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    const updateWidth = () => {
      if (divRef.current) {
        setWidth(divRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);

    return () => window.removeEventListener("resize", updateWidth);
  }, [window?.innerWidth, width]);

  return (
    <div ref={divRef} className={`pt-3 bg-white !important  `}>
      <div className="lg:flex items-center justify-between text-sm pl-4 pb-2 lg:pb-4">
        <div className="relative border border-gray-300 lg:w-1/2 flex justify-between">
          <Input
            placeholder="Search for event ID , Borrower Name"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="outline-none px-2 w-full font-openSans font-normal text-xs leading-4 text-brand-textLightGrey  tracking-wider "
          />
          <div className="m-2 border-l-[2px] flex justify-center items-center">
            <Button className="text-brand-bgBlack bg-white rounded-md  hover:bg-white h-5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-gray-600"
                viewBox="0 0 15 16"
                fill="none"
              >
                <path
                  d="M14.5477 14.4534L10.935 10.8407C11.9652 9.65601 12.4952 8.11756 12.4132 6.5497C12.3312 4.98183 11.6437 3.50705 10.4956 2.43622C9.34741 1.36539 7.82835 0.782173 6.25858 0.809513C4.68881 0.836853 3.19098 1.47261 2.08082 2.58277C0.970658 3.69293 0.3349 5.19077 0.30756 6.76053C0.28022 8.3303 0.863434 9.84936 1.93426 10.9975C3.00509 12.1457 4.47988 12.8332 6.04774 12.9152C7.61561 12.9972 9.15406 12.4672 10.3388 11.437L13.9515 15.0497C14.0314 15.1242 14.1372 15.1647 14.2465 15.1628C14.3558 15.1609 14.4601 15.1166 14.5374 15.0393C14.6147 14.962 14.6589 14.8577 14.6609 14.7484C14.6628 14.6392 14.6222 14.5334 14.5477 14.4534ZM1.17145 6.87653C1.17145 5.84745 1.47661 4.84148 2.04834 3.98583C2.62006 3.13018 3.43268 2.46328 4.38343 2.06947C5.33417 1.67566 6.38035 1.57262 7.38966 1.77338C8.39896 1.97414 9.32607 2.46969 10.0537 3.19736C10.7814 3.92503 11.277 4.85214 11.4777 5.86145C11.6785 6.87076 11.5754 7.91693 11.1816 8.86768C10.7878 9.81843 10.1209 10.631 9.26528 11.2028C8.40963 11.7745 7.40366 12.0797 6.37458 12.0797C4.99514 12.078 3.67267 11.5293 2.69726 10.5538C1.72185 9.57843 1.17313 8.25597 1.17145 6.87653Z"
                  fill="#666666"
                />
              </svg>
            </Button>
          </div>
        </div>
        <div className="w-full lg:w-fit pr-[16px] mt-4 lg:mt-0">
          {searchComponent}
          {filterComponent}
        </div>
      </div>
      <div className="mx-auto !important" style={{ width: `${width - 30}px` }}>
        <Table>
          <TableHeader className="border text-xs">
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {group.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          {loading ? (
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={columns.length + (actions ? 1 : 0)}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody className="text-xs font-normal">
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="py-0">
                    {row.getVisibleCells().map((cell, index) => {
                      return (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })}
                    {actions && <TableCell>{actions(row.original)}</TableCell>}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (actions ? 1 : 0)}
                    className="h-24 text-center"
                  >
                    No Bidders found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </div>

      <div className="flex items-center justify-between space-x-2 py-4 px-4">
        <p>Total Bidders - {data.length}</p>
        <div className="flex space-x-4">
          <Button
            variant="outline"
            className="text-xs"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>

          <Button
            variant="outline"
            className="text-xs"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
